import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  PageHeader, Segmented, Button, IconButton, Icon, Tag, Banner, Panel, EmptyState, Skeleton,
  Dialog, FileDrop, StatGrid, StatTile, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { apiError } from '../../next/ui';
import {
  TRACKS, TRACK_BY_FRAME, WEEKDAYS, WEEKDAY_LETTERS, dateKey, fromKey, addDays, daysBetween, israelTodayKey,
  hebrewOf, gregLabel, gregMonthShort, cursorFor, shiftCursor, monthTitle, monthWeeks, monthBounds,
  eventKind, fallbackEvent, trackText, isReview, tractateProgress, trackTractate, yearSegments, upcomingExams,
  weekParasha, inDaysLabel,
} from './calendarModel';
import './studyCalendar.css';

/**
 * לוח שנה — Hebrew + Gregorian calendar with the yearly study plan (דף היומי / דף השינון / משננים),
 * holidays, parashot and exam days. Built on crm-kit/next tokens only, so it follows every style × mode;
 * the old design renders it inside a light ck scope (StudyCalendarLegacy).
 *
 * StudyCalendarBoard is the calendar itself and is shared with the members' portal (CalendarScreen), which
 * feeds it its own loader and tints the tokens with the portal theme. The whole plan is loaded once
 * (a year ≈ 450 small rows), so month navigation, the year map and "next exam" are instant and client-side.
 */
const DEFAULT_PREFS_KEY = 'bchinot-study-calendar';
const KIND_TONE = { yomtov: 'danger', fast: 'warning', festive: 'info', note: 'neutral' };
const MAX_FETCH_DAYS = 800;

/** view choices per viewer; no key → not kept (shared station tablets) */
function readPrefs(key) {
  if (!key) return {};
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
}
function writePrefs(key, patch) {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify({ ...readPrefs(key), ...patch })); } catch { /* storage blocked */ }
}

/** the admin loader: plan range, then the whole plan (capped) */
async function loadAdminPlan() {
  const { data: meta } = await api.get('/study-calendar/meta');
  if (!meta.range?.count) return { range: meta.range, days: [] };
  const to = daysBetween(meta.range.from, meta.range.to) > MAX_FETCH_DAYS
    ? dateKey(addDays(fromKey(meta.range.from), MAX_FETCH_DAYS)) : meta.range.to;
  const { data } = await api.get('/study-calendar', { params: { from: meta.range.from, to } });
  return { range: meta.range, days: data.data || [] };
}

/** width classes of the board — from its own width, so it fits the admin page and the portal's narrow column */
function useWidthClass(ref) {
  const [width, setWidth] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  if (width == null) return '';
  return [width >= 1180 ? 'scal--wide' : '', width < 1000 ? 'scal--md' : '', width < 700 ? 'scal--sm' : ''].filter(Boolean).join(' ');
}

/** לוח שנה — admin screen: header, import, and the shared board */
export default function StudyCalendar() {
  const { hasPermission } = useAuth();
  const canImport = hasPermission('studyCalendar', 'update');
  const todayKey = useMemo(() => israelTodayKey(), []);
  const todayHeb = hebrewOf(fromKey(todayKey));
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <>
      <PageHeader
        title="לוח שנה"
        sub={`יום ${WEEKDAYS[fromKey(todayKey).getDay()]}, ${todayHeb.label} · ${gregLabel(fromKey(todayKey))}`}
        primary={canImport ? { label: 'ייבוא תוכנית', icon: 'upload', onClick: () => setImportOpen(true) } : undefined}
      />
      <StudyCalendarBoard
        loadPlan={loadAdminPlan}
        reloadKey={reloadKey}
        canImport={canImport}
        onImport={() => setImportOpen(true)}
      />
      {canImport ? <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={() => setReloadKey((n) => n + 1)} /> : null}
    </>
  );
}

/**
 * StudyCalendarBoard — today, the month (grid / list), the selected day and the year map.
 *   loadPlan()             async → { range, days }
 *   reloadKey              bump to reload
 *   canImport / onImport   empty-state action (admin)
 *   onOpenExam(frame)      exam rows link to the exams of that frame (portal)
 *   prefsKey               localStorage key of the viewer's view choices
 */
export function StudyCalendarBoard({ loadPlan, reloadKey = 0, canImport = false, onImport, onOpenExam, prefsKey = DEFAULT_PREFS_KEY }) {
  const todayKey = useMemo(() => israelTodayKey(), []);
  const prefs = useMemo(() => readPrefs(prefsKey), [prefsKey]);
  const rootRef = useRef(null);
  const widthClass = useWidthClass(rootRef);

  const [range, setRange] = useState(null);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calMode, setCalMode] = useState(prefs.calMode === 'greg' ? 'greg' : 'hebrew');
  const [layout, setLayout] = useState(prefs.layout === 'list' ? 'list' : 'grid');
  const [shown, setShown] = useState(() => new Set(Array.isArray(prefs.tracks) ? prefs.tracks : TRACKS.map((t) => t.key)));
  const [cursor, setCursor] = useState(() => cursorFor(prefs.calMode === 'greg' ? 'greg' : 'hebrew', fromKey(todayKey)));
  const [selected, setSelected] = useState(todayKey);
  const [slide, setSlide] = useState('');
  const loadRef = useRef(loadPlan);
  loadRef.current = loadPlan;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const plan = await loadRef.current();
      setRange(plan?.range || null);
      setDays(plan?.days || []);
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת לוח השנה'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const byDate = useMemo(() => new Map(days.map((d) => [d.date, d])), [days]);
  const segments = useMemo(() => yearSegments(days), [days]);
  const weeks = useMemo(() => monthWeeks(cursor), [cursor]);
  const { title, sub } = monthTitle(cursor);
  const visibleTracks = TRACKS.filter((t) => shown.has(t.key));

  const go = useCallback((delta) => {
    setSlide(delta > 0 ? 'is-next' : 'is-prev');
    setCursor((c) => shiftCursor(c, delta));
  }, []);
  const goToDate = useCallback((key) => {
    const target = cursorFor(calMode, fromKey(key));
    setSlide(key > dateKey(monthBounds(cursor)[0]) ? 'is-next' : 'is-prev');
    setCursor(target);
    setSelected(key);
  }, [calMode, cursor]);
  const goToday = () => goToDate(todayKey);

  const changeMode = (mode) => {
    setCalMode(mode);
    writePrefs(prefsKey, { calMode: mode });
    setCursor(cursorFor(mode, fromKey(selected || todayKey)));
    setSlide('');
  };
  const changeLayout = (v) => { setLayout(v); writePrefs(prefsKey, { layout: v }); };
  const toggleTrack = (key) => {
    setShown((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      writePrefs(prefsKey, { tracks: [...next] });
      return next;
    });
  };

  // ← / → months (RTL: left is forward), Home / ה → today
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) || e.target?.isContentEditable) return;
      if (document.querySelector('.ck-dialog, [role="dialog"]')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(-1); }
      else if (e.key === 'Home' || e.key === 'ה') { e.preventDefault(); goToday(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const busy = loading && !days.length;

  return (
    <div className={`scal ${widthClass}`.trim()} ref={rootRef}>

      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{error}</Banner> : null}

      <TodayHero
        todayKey={todayKey}
        byDate={byDate}
        days={days}
        range={range}
        loading={busy}
        canImport={canImport}
        onImport={onImport}
        onPick={goToDate}
      />

      <section className="scal-board" aria-label="לוח חודשי">
        <div className="scal-toolbar">
          <div className="scal-nav">
            <IconButton icon="right" label="החודש הקודם" onClick={() => go(-1)} />
            <div className="scal-title" aria-live="polite">
              <h2>{title}</h2>
              <span>{sub}</span>
            </div>
            <IconButton icon="left" label="החודש הבא" onClick={() => go(1)} />
            <Button size="sm" variant="ghost" icon="calendar" onClick={goToday}>היום</Button>
          </div>
          <div className="scal-controls">
            <div className="scal-filters" role="group" aria-label="מסלולים מוצגים">
              {TRACKS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`scal-filter scal-tone--${t.tone} ${shown.has(t.key) ? 'is-on' : ''}`}
                  aria-pressed={shown.has(t.key)}
                  onClick={() => toggleTrack(t.key)}
                >
                  <i aria-hidden="true" />
                  {t.label}
                </button>
              ))}
            </div>
            <Segmented items={[{ value: 'hebrew', label: 'חודש עברי' }, { value: 'greg', label: 'חודש לועזי' }]} value={calMode} onChange={changeMode} ariaLabel="סוג חודש" />
            <Segmented items={[{ value: 'grid', label: 'לוח' }, { value: 'list', label: 'רשימה' }]} value={layout} onChange={changeLayout} ariaLabel="תצוגה" />
          </div>
        </div>

        <div className="scal-main">
          <div className="scal-month">
            {busy ? <SkeletonRows n={6} height={72} /> : layout === 'grid' ? (
              <MonthGrid
                key={`${cursor.mode}-${cursor.year}-${cursor.month}`}
                slide={slide}
                weeks={weeks}
                mode={calMode}
                byDate={byDate}
                todayKey={todayKey}
                selected={selected}
                tracks={visibleTracks}
                onSelect={setSelected}
              />
            ) : (
              <AgendaList
                key={`${cursor.mode}-${cursor.year}-${cursor.month}`}
                slide={slide}
                weeks={weeks}
                byDate={byDate}
                todayKey={todayKey}
                selected={selected}
                tracks={visibleTracks}
                onSelect={setSelected}
              />
            )}
            <Legend />
          </div>
          <DayPanel
            dayKey={selected}
            byDate={byDate}
            todayKey={todayKey}
            hasPlan={!!days.length}
            onOpenExam={onOpenExam}
            onStep={(n) => {
              const key = dateKey(addDays(fromKey(selected), n));
              const [first, last] = monthBounds(cursor);
              if (fromKey(key) < first || fromKey(key) > last) goToDate(key); else setSelected(key);
            }}
          />
        </div>
      </section>

      {days.length ? <YearMap segments={segments} days={days} todayKey={todayKey} onJump={goToDate} /> : null}
    </div>
  );
}

function SkeletonRows({ n = 3, height = 60 }) {
  return (
    <div className="scal-skeleton">
      {Array.from({ length: n }, (_, i) => <Skeleton key={i} height={height} radius={10} />)}
    </div>
  );
}

/* ═════════════════════════ today ═════════════════════════ */

function TodayHero({ todayKey, byDate, days, range, loading, canImport, onImport, onPick }) {
  const date = fromKey(todayKey);
  const heb = hebrewOf(date);
  const day = byDate.get(todayKey);
  const event = day?.event || (!day ? fallbackEvent(date) : '');
  const parasha = weekParasha(byDate, todayKey);
  const exams = useMemo(() => upcomingExams(days, todayKey, 4), [days, todayKey]);

  let body;
  if (loading) body = <SkeletonRows n={3} height={84} />;
  else if (day) {
    body = (
      <div className="scal-hero__tracks">
        {TRACKS.map((t) => <TrackTile key={t.key} track={t} day={day} />)}
      </div>
    );
  } else if (!days.length) {
    body = (
      <EmptyState
        compact
        icon="calendar"
        title="עדיין לא נטענה תוכנית לימודים"
        action={canImport ? <Button variant="primary" icon="upload" onClick={onImport}>ייבוא תוכנית מאקסל</Button> : null}
      >
        התאריכים העבריים והחגים מוצגים; ההספקים יופיעו אחרי ייבוא התוכנית.
      </EmptyState>
    );
  } else {
    body = (
      <EmptyState compact icon="calendar" title="היום אינו בטווח התוכנית">
        {`התוכנית מכסה ${gregLabel(fromKey(range.from), { short: true })} – ${gregLabel(fromKey(range.to), { short: true })}`}
      </EmptyState>
    );
  }

  return (
    <section className="scal-hero" aria-label="היום">
      <div className="scal-hero__date">
        <span className="scal-hero__kicker">היום · יום {WEEKDAYS[date.getDay()]}</span>
        <div className="scal-hero__day">{heb.dayGem}</div>
        <div className="scal-hero__month">{`${heb.monthName} ${heb.yearGem}`}</div>
        <div className="scal-hero__greg">{gregLabel(date)}</div>
        <div className="scal-hero__tags">
          {parasha ? <Tag tone="primary" icon="book">{`פרשת ${parasha}`}</Tag> : null}
          {event ? <Tag tone={KIND_TONE[eventKind(event)] || 'neutral'} icon="sparkle">{event}</Tag> : null}
          {day?.note ? <Tag tone="warning" icon="flag">{day.note}</Tag> : null}
        </div>
      </div>
      <div className="scal-hero__body">
        {body}
        {exams.length ? (
          <div className="scal-hero__exams">
            <span className="scal-hero__label"><Icon name="task" size={15} /> המבחנים הקרובים</span>
            <div className="scal-hero__exam-list">
              {exams.map((e) => {
                const t = TRACK_BY_FRAME[e.track];
                return (
                  <button key={`${e.date}-${e.track}`} type="button" className={`scal-exam-chip scal-tone--${t?.tone || 'neutral'}`} onClick={() => onPick(e.date)}>
                    <b>{inDaysLabel(e.inDays)}</b>
                    <span>{`${t?.label || ''} · ${e.text}`}</span>
                    <small>{`יום ${WEEKDAYS[fromKey(e.date).getDay()]}, ${hebrewOf(fromKey(e.date)).dayGem} ב${hebrewOf(fromKey(e.date)).monthName}`}</small>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function TrackTile({ track, day }) {
  const v = day?.[track.key];
  const text = trackText(day, track.key);
  const review = isReview(day, track.key);
  const exam = day?.exams?.find((e) => e.track === track.frame);
  const progress = !review ? tractateProgress(trackTractate(day, track.key), track.key === 'shinun' ? v?.to?.daf : v?.daf) : null;
  return (
    <div className={`scal-tile scal-tone--${track.tone} ${!text ? 'is-empty' : ''}`}>
      <div className="scal-tile__head">
        <span className="scal-tile__icon"><Icon name={track.icon} size={16} /></span>
        <span className="scal-tile__label">{track.label}</span>
        {exam ? <Tag tone="warning" icon="task">מבחן היום</Tag> : null}
      </div>
      <div className="scal-tile__text">{text ? (review ? 'יום שינון וחזרה' : text) : 'אין הספק היום'}</div>
      {exam ? <div className="scal-tile__sub">{`מבחן על ${exam.text}`}</div> : null}
      {progress ? (
        <div className="scal-tile__progress">
          <span className="scal-progress" role="progressbar" aria-valuenow={progress.pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${progress.pct}%` }} /></span>
          <small>{`${progress.pct}% ממסכת ${progress.tractate}`}</small>
        </div>
      ) : null}
    </div>
  );
}

/* ═════════════════════════ month ═════════════════════════ */

function cellInfo(cell, byDate) {
  const day = byDate.get(cell.key);
  const event = day ? day.event : fallbackEvent(cell.date);
  return { day, event, kind: eventKind(event), heb: hebrewOf(cell.date) };
}

function MonthGrid({ weeks, mode, byDate, todayKey, selected, tracks, onSelect, slide }) {
  const onKeyDown = (e, key) => {
    const step = { ArrowLeft: 1, ArrowRight: -1, ArrowDown: 7, ArrowUp: -7 }[e.key];
    if (step) {
      e.preventDefault();
      e.stopPropagation();
      const next = dateKey(addDays(fromKey(key), step));
      const el = e.currentTarget.closest('.scal-grid')?.querySelector(`[data-key="${next}"]`);
      if (el) { onSelect(next); el.focus(); }
    }
  };
  return (
    <div className={`scal-grid ${slide}`} role="grid" aria-label="ימי החודש">
      <div className="scal-grid__head" role="row">
        {WEEKDAYS.map((w, i) => (
          <div key={w} role="columnheader" className={i === 6 ? 'is-shabbat' : ''}>
            <span className="scal-wide">{w === 'שבת' ? 'שבת קודש' : `יום ${w}`}</span>
            <span className="scal-narrow">{WEEKDAY_LETTERS[i]}</span>
          </div>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0].key} className="scal-grid__row" role="row">
          {week.map((cell) => {
            const { day, event, kind, heb } = cellInfo(cell, byDate);
            const isToday = cell.key === todayKey;
            const isSel = cell.key === selected;
            const greg = cell.date.getDate();
            const big = mode === 'greg' ? greg : heb.dayGem;
            const small = mode === 'greg'
              ? (heb.day === 1 ? `${heb.dayGem} ${heb.monthName}` : heb.dayGem)
              : (greg === 1 ? `${greg} ${gregMonthShort(cell.date)}` : greg);
            const exams = day?.exams || [];
            return (
              <div
                key={cell.key}
                role="gridcell"
                data-key={cell.key}
                tabIndex={isSel ? 0 : -1}
                aria-selected={isSel}
                aria-label={`${heb.label}, ${gregLabel(cell.date)}${event ? `, ${event}` : ''}`}
                className={[
                  'scal-cell',
                  cell.inMonth ? '' : 'is-out',
                  cell.date.getDay() === 6 ? 'is-shabbat' : '',
                  kind ? `is-${kind}` : '',
                  isToday ? 'is-today' : '',
                  isSel ? 'is-selected' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => onSelect(cell.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(cell.key); }
                  onKeyDown(e, cell.key);
                }}
              >
                <div className="scal-cell__top">
                  <span className="scal-cell__num">{big}</span>
                  <span className="scal-cell__alt">{small}</span>
                </div>
                {cell.date.getDay() === 6 && day?.parasha ? <div className="scal-cell__parasha">{day.parasha}</div> : null}
                {event ? <div className={`scal-cell__event is-${kind}`} title={event}>{event}</div> : null}
                {day?.note ? <div className="scal-cell__event is-milestone" title={day.note}>★ {day.note}</div> : null}
                <div className="scal-cell__lines">
                  {tracks.map((t) => {
                    const text = trackText(day, t.key);
                    if (!text) return null;
                    const review = isReview(day, t.key);
                    return (
                      <div key={t.key} className={`scal-line scal-tone--${t.tone} ${review ? 'is-review' : ''}`} title={`${t.label}: ${text}`}>
                        {review ? 'חזרה' : text}
                      </div>
                    );
                  })}
                  {exams.length ? (
                    <div className="scal-cell__exam" title={exams.map((e) => `${TRACK_BY_FRAME[e.track]?.label}: ${e.text}`).join('\n')}>
                      <Icon name="task" size={12} />
                      {`מבחן${exams.length > 1 ? ` ×${exams.length}` : ''}`}
                    </div>
                  ) : null}
                </div>
                <div className="scal-cell__dots" aria-hidden="true">
                  {tracks.map((t) => (trackText(day, t.key) && !isReview(day, t.key) ? <i key={t.key} className={`scal-tone--${t.tone}`} /> : null))}
                  {exams.length ? <i className="scal-tone--warning is-exam" /> : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function AgendaList({ weeks, byDate, todayKey, selected, tracks, onSelect, slide }) {
  const cells = weeks.flat().filter((c) => c.inMonth);
  return (
    <div className={`scal-agenda ${slide}`}>
      {cells.map((cell) => {
        const { day, event, kind, heb } = cellInfo(cell, byDate);
        const wd = cell.date.getDay();
        return (
          <React.Fragment key={cell.key}>
            {wd === 0 && cell !== cells[0] ? <div className="scal-agenda__week" aria-hidden="true" /> : null}
            <button
              type="button"
              className={[
                'scal-agenda__row',
                wd === 6 ? 'is-shabbat' : '',
                kind ? `is-${kind}` : '',
                cell.key === todayKey ? 'is-today' : '',
                cell.key === selected ? 'is-selected' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onSelect(cell.key)}
            >
              <span className="scal-agenda__date">
                <b>{heb.dayGem}</b>
                <small>{`${WEEKDAY_LETTERS[wd]} · ${cell.date.getDate()} ${gregMonthShort(cell.date)}`}</small>
              </span>
              <span className="scal-agenda__body">
                <span className="scal-agenda__tags">
                  {wd === 6 && day?.parasha ? <Tag tone="primary">{`פרשת ${day.parasha}`}</Tag> : null}
                  {event ? <Tag tone={KIND_TONE[kind] || 'neutral'}>{event}</Tag> : null}
                  {day?.note ? <Tag tone="warning" icon="flag">{day.note}</Tag> : null}
                  {(day?.exams || []).map((e) => <Tag key={e.track} tone="warning" icon="task">{`מבחן ${TRACK_BY_FRAME[e.track]?.label}: ${e.text}`}</Tag>)}
                </span>
                <span className="scal-agenda__lines">
                  {tracks.map((t) => {
                    const text = trackText(day, t.key);
                    if (!text) return null;
                    return (
                      <span key={t.key} className={`scal-line scal-tone--${t.tone} ${isReview(day, t.key) ? 'is-review' : ''}`}>
                        <em>{t.short}</em>
                        {text}
                      </span>
                    );
                  })}
                </span>
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Legend() {
  return (
    <div className="scal-legend" aria-label="מקרא">
      <span><i className="scal-sw is-yomtov" />יום טוב</span>
      <span><i className="scal-sw is-festive" />מועד / ראש חודש</span>
      <span><i className="scal-sw is-fast" />תענית</span>
      <span><i className="scal-sw is-exam" />יום מבחן</span>
      <span className="scal-legend__keys">
        <kbd>←</kbd>
        <kbd>→</kbd>
        {' חודשים · '}
        <kbd>Home</kbd>
        {' היום'}
      </span>
    </div>
  );
}

/* ═════════════════════════ selected day ═════════════════════════ */

function DayPanel({ dayKey, byDate, todayKey, hasPlan, onStep, onOpenExam }) {
  const date = fromKey(dayKey);
  const heb = hebrewOf(date);
  const day = byDate.get(dayKey);
  const event = day ? day.event : fallbackEvent(date);
  const parasha = weekParasha(byDate, dayKey);
  const rel = daysBetween(todayKey, dayKey);
  const relLabel = rel === 0 ? 'היום' : rel === -1 ? 'אתמול' : rel === 1 ? 'מחר' : rel > 0 ? `בעוד ${rel} ימים` : `לפני ${-rel} ימים`;

  return (
    <aside className="scal-day" aria-label="פרטי היום" aria-live="polite">
      <div className="scal-day__head">
        <IconButton icon="right" label="היום הקודם" size="sm" onClick={() => onStep(-1)} />
        <div className="scal-day__title">
          <small>{`יום ${WEEKDAYS[date.getDay()]} · ${relLabel}`}</small>
          <h3>{heb.label}</h3>
          <span>{gregLabel(date)}</span>
        </div>
        <IconButton icon="left" label="היום הבא" size="sm" onClick={() => onStep(1)} />
      </div>

      {(parasha || event || day?.note) ? (
        <div className="scal-day__tags">
          {parasha ? <Tag tone="primary" icon="book">{`${date.getDay() === 6 ? 'פרשת' : 'פרשת השבוע:'} ${parasha}`}</Tag> : null}
          {event ? <Tag tone={KIND_TONE[eventKind(event)] || 'neutral'} icon="sparkle">{event}</Tag> : null}
          {day?.note ? <Tag tone="warning" icon="flag">{day.note}</Tag> : null}
        </div>
      ) : null}

      {day ? (
        <div className="scal-day__tracks">
          {TRACKS.map((t) => {
            const text = trackText(day, t.key);
            const review = isReview(day, t.key);
            const exam = day.exams?.find((e) => e.track === t.frame);
            const v = day[t.key];
            const progress = text && !review ? tractateProgress(trackTractate(day, t.key), t.key === 'shinun' ? v?.to?.daf : v?.daf) : null;
            return (
              <div key={t.key} className={`scal-day__track scal-tone--${t.tone} ${!text ? 'is-empty' : ''}`}>
                <span className="scal-tile__icon"><Icon name={t.icon} size={15} /></span>
                <div>
                  <small>{t.label}</small>
                  <b>{text ? (review ? 'שינון וחזרה' : text) : '—'}</b>
                  {exam ? <span className="scal-day__exam"><Icon name="task" size={13} />{`מבחן: ${exam.text}`}</span> : null}
                  {exam && onOpenExam ? (
                    <button type="button" className="scal-day__link" onClick={() => onOpenExam(t.frame)}>
                      {`למבחני ${t.label}`}
                      <Icon name="left" size={13} />
                    </button>
                  ) : null}
                  {progress ? <span className="scal-day__progress">{`דף ${progress.daf} מתוך ${progress.max} · ${progress.pct}%`}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="scal-day__empty">{hasPlan ? 'יום זה אינו בטווח תוכנית הלימודים.' : 'לא נטענה תוכנית לימודים.'}</p>
      )}
    </aside>
  );
}

/* ═════════════════════════ year map ═════════════════════════ */

function YearMap({ segments, days, todayKey, onJump }) {
  const from = days[0].date;
  const to = days[days.length - 1].date;
  const total = daysBetween(from, to) + 1;
  const pos = (key) => (daysBetween(from, key) / total) * 100;
  const todayPct = todayKey >= from && todayKey <= to ? pos(todayKey) : null;

  // Rosh Chodesh ticks
  const ticks = [];
  let c = cursorFor('hebrew', fromKey(from));
  for (let i = 0; i < 30; i += 1) {
    const first = dateKey(monthBounds(c)[0]);
    if (first > to) break;
    if (first >= from) ticks.push({ key: first, label: hebrewOf(fromKey(first)).monthName, pct: pos(first) });
    c = shiftCursor(c, 1);
  }

  return (
    <Panel title="מפת השנה" sub="המסכתות של כל מסלול לאורך התוכנית — לחיצה מעבירה לחודש" icon="chart">
      <div className="scal-map">
        <div className="scal-map__ticks" aria-hidden="true">
          {ticks.map((t) => <span key={t.key} style={{ insetInlineStart: `${t.pct}%` }}>{t.label}</span>)}
        </div>
        {TRACKS.map((t) => (
          <div key={t.key} className="scal-map__row">
            <span className="scal-map__label">{t.label}</span>
            <div className={`scal-map__bar scal-tone--${t.tone}`}>
              {(segments[t.key] || []).map((s) => (
                <button
                  key={`${s.tractate}-${s.from}`}
                  type="button"
                  title={`${s.tractate} · ${hebrewOf(fromKey(s.from)).label} – ${hebrewOf(fromKey(s.to)).label}`}
                  className={`scal-map__seg ${todayKey >= s.from && todayKey <= s.to ? 'is-now' : ''}`}
                  style={{ insetInlineStart: `${pos(s.from)}%`, width: `${(s.days / total) * 100}%` }}
                  onClick={() => onJump(s.from)}
                  aria-label={`${t.label}: ${s.tractate}`}
                >
                  <span>{s.tractate}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
        {todayPct != null ? (
          <div className="scal-map__today" style={{ insetInlineStart: `calc(var(--scal-map-label) + (100% - var(--scal-map-label)) * ${todayPct / 100})` }}>
            <span>היום</span>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

/* ═════════════════════════ import ═════════════════════════ */

function ImportDialog({ open, onClose, onDone }) {
  const toast = useCkToast();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    if (!open) { setFile(null); setPreview(null); setError(''); setBusy(false); }
  }, [open]);

  const send = (f, dryRun) => {
    const fd = new FormData();
    fd.append('file', f);
    return api.post(`/study-calendar/import${dryRun ? '?dryRun=1' : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const pick = async (f) => {
    if (!f) return;
    const my = ++seq.current;
    setFile(f);
    setPreview(null);
    setError('');
    setBusy(true);
    try {
      const { data } = await send(f, true);
      if (my === seq.current) setPreview(data);
    } catch (e) {
      if (my === seq.current) setError(apiError(e, 'לא ניתן לקרוא את הקובץ'));
    } finally {
      if (my === seq.current) setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await send(file, false);
      toast.success(`התוכנית נשמרה — ${data.created} ימים חדשים, ${data.updated} עודכנו`);
      onClose();
      onDone();
    } catch (e) {
      setError(apiError(e, 'הייבוא נכשל'));
    } finally {
      setBusy(false);
    }
  };

  const changes = preview ? preview.created + preview.updated : 0;
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      title="ייבוא תוכנית לימודים"
      icon="upload"
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>ביטול</Button>
          <Button variant="primary" icon="check" onClick={commit} disabled={busy || !changes}>
            {changes ? `שמירת ${changes} ימים` : 'שמירה'}
          </Button>
        </>
      )}
    >
      <div className="scal-import">
        <p className="nx-desc">
          קובץ אקסל בפורמט "תוכנית תשפ״ז": עמודות לועזי שנה / חדש / יום, תאריך עברי, פרשה, אירוע, דף היומי, דף השינון,
          משננים ועמודות המבחנים. ימים קיימים מתעדכנים לפי התאריך; ימים שאינם בקובץ נשארים כמו שהם.
        </p>
        <FileDrop accept=".xlsx,.xls" onFile={pick} loading={busy && !preview} hint={file ? file.name : 'xlsx'} />
        {error ? <Banner tone="danger" icon="alert">{error}</Banner> : null}
        {preview ? (
          <>
            <StatGrid min={120}>
              <StatTile label="ימים בקובץ" value={preview.total} icon="calendar" tone="primary" />
              <StatTile label="חדשים" value={preview.created} icon="plus" tone="success" />
              <StatTile label="יתעדכנו" value={preview.updated} icon="edit" tone="warning" />
              <StatTile label="ללא שינוי" value={preview.unchanged} icon="check" tone="neutral" />
            </StatGrid>
            <Banner tone={changes ? 'info' : 'success'} icon="calendar">
              {`${gregLabel(fromKey(preview.from))} – ${gregLabel(fromKey(preview.to))}`}
              {` · ${hebrewOf(fromKey(preview.from)).label} – ${hebrewOf(fromKey(preview.to)).label}`}
              {changes ? '' : ' · התוכנית כבר מעודכנת'}
            </Banner>
            {preview.errors?.length ? (
              <Banner tone="warning" icon="alert">
                {`${preview.errors.length} שורות דולגו: `}
                {preview.errors.slice(0, 6).map((e) => `שורה ${e.row} — ${e.error}`).join(' · ')}
              </Banner>
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
